import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, doc, onSnapshot, runTransaction, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useLang } from '../LangContext';
import { AccessDenied, canAccess } from '../adminAccess';
import Table from '../components/Table';
import { buildLocalizedTextPayload } from '../localizedTextPayload';

const labelsByLang = {
  en: {
    title: 'Service Packages',
    description: 'The live, admin-managed catalog: city × grave size × care type. Every field is free text — a new grave size or care type appears in the customer app the moment you create it here, with no app update. Existing orders keep the package details they were created with, unaffected by any later edit.',
    city: 'City',
    graveSize: 'Grave size',
    graveSizePlaceholder: 'e.g. Tək məzar',
    careType: 'Care type',
    careTypePlaceholder: 'e.g. Klassik Baxım',
    green: 'Green area',
    greenChoose: '— choose —',
    greenYes: 'Yes',
    greenNo: 'No',
    price: 'Price (₼)',
    descAz: 'Description (AZ)',
    descRu: 'Description (RU)',
    descEn: 'Description (EN)',
    create: 'Create entry',
    creating: 'Creating…',
    status: 'Status',
    active: 'Active',
    inactive: 'Deactivated',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    noData: 'No entries for this city yet',
    allCities: 'All cities',
    errCityRequired: 'Choose a city.',
    errGraveSizeRequired: 'Enter a grave size.',
    errCareTypeRequired: 'Enter a care type.',
    errGreenRequired: 'Choose whether this entry has a green area.',
    errPriceInvalid: 'Enter a price greater than 0.',
    errExists: 'An entry for this city, grave size, care type and green-area choice already exists.',
    confirmDeactivate: 'Deactivate this entry? Customers will no longer be able to select it for a new order.',
    seedDefaults: 'Seed default 12 packages',
    seeding: 'Seeding…',
    seedDone: (created, skipped) => `Seeded ${created} new entr${created === 1 ? 'y' : 'ies'}${skipped ? `, skipped ${skipped} already present` : ''}.`,
    seedNoCities: 'Could not find both Bakı and Gəncə in the cities collection — seed those cities first.',
    entry: 'Grave size / Care type',
    greenCol: 'Green area',
    priceCol: 'Price',
  },
  az: {
    title: 'Xidmət Paketləri',
    description: 'Canlı, admin idarə edilən kataloq: şəhər × məzar ölçüsü × qayğı növü. Hər sahə sərbəst mətndir — burada yaratdığınız kimi yeni məzar ölçüsü və ya qayğı növü heç bir tətbiq yeniləməsi olmadan müştəri tətbiqində dərhal görünür. Mövcud sifarişlər yaradıldığı andakı paket məlumatlarını saxlayır, sonrakı heç bir redaktədən təsirlənmir.',
    city: 'Şəhər',
    graveSize: 'Məzar ölçüsü',
    graveSizePlaceholder: 'məs. Tək məzar',
    careType: 'Qayğı növü',
    careTypePlaceholder: 'məs. Klassik Baxım',
    green: 'Yaşıllıq sahəsi',
    greenChoose: '— seçin —',
    greenYes: 'Var',
    greenNo: 'Yoxdur',
    price: 'Qiymət (₼)',
    descAz: 'Təsvir (AZ)',
    descRu: 'Təsvir (RU)',
    descEn: 'Təsvir (EN)',
    create: 'Yeni əlavə et',
    creating: 'Yaradılır…',
    status: 'Status',
    active: 'Aktiv',
    inactive: 'Deaktiv',
    deactivate: 'Deaktiv et',
    reactivate: 'Aktivləşdir',
    edit: 'Redaktə et',
    save: 'Yadda saxla',
    cancel: 'Ləğv et',
    noData: 'Bu şəhər üçün hələ heç nə yoxdur',
    allCities: 'Bütün şəhərlər',
    errCityRequired: 'Şəhər seçin.',
    errGraveSizeRequired: 'Məzar ölçüsü daxil edin.',
    errCareTypeRequired: 'Qayğı növü daxil edin.',
    errGreenRequired: 'Bu əlavənin yaşıllıq sahəsi olub-olmadığını seçin.',
    errPriceInvalid: '0-dan böyük qiymət daxil edin.',
    errExists: 'Bu şəhər, məzar ölçüsü, qayğı növü və yaşıllıq sahəsi seçimi üçün əlavə artıq mövcuddur.',
    confirmDeactivate: 'Bu əlavə deaktiv edilsin? Müştərilər onu yeni sifariş üçün seçə bilməyəcək.',
    seedDefaults: 'Standart 12 paketi yarat',
    seeding: 'Yaradılır…',
    seedDone: (created, skipped) => `${created} yeni əlavə yaradıldı${skipped ? `, ${skipped} onsuz da mövcud idi` : ''}.`,
    seedNoCities: 'cities kolleksiyasında Bakı və Gəncə tapılmadı — əvvəlcə bu şəhərləri əlavə edin.',
    entry: 'Məzar ölçüsü / Qayğı növü',
    greenCol: 'Yaşıllıq sahəsi',
    priceCol: 'Qiymət',
  },
  ru: {
    title: 'Пакеты услуг',
    description: 'Живой каталог, управляемый администратором: город × размер могилы × тип ухода. Каждое поле — свободный текст: новый размер могилы или тип ухода появляется в приложении клиента сразу после создания здесь, без обновления приложения. Существующие заказы сохраняют данные пакета на момент создания и не затрагиваются последующим редактированием.',
    city: 'Город',
    graveSize: 'Размер могилы',
    graveSizePlaceholder: 'напр. Tək məzar',
    careType: 'Тип ухода',
    careTypePlaceholder: 'напр. Klassik Baxım',
    green: 'Зелёная зона',
    greenChoose: '— выбрать —',
    greenYes: 'Есть',
    greenNo: 'Нет',
    price: 'Цена (₼)',
    descAz: 'Описание (AZ)',
    descRu: 'Описание (RU)',
    descEn: 'Описание (EN)',
    create: 'Создать запись',
    creating: 'Создание…',
    status: 'Статус',
    active: 'Активен',
    inactive: 'Деактивирован',
    deactivate: 'Деактивировать',
    reactivate: 'Активировать',
    edit: 'Редактировать',
    save: 'Сохранить',
    cancel: 'Отмена',
    noData: 'Для этого города пока нет записей',
    allCities: 'Все города',
    errCityRequired: 'Выберите город.',
    errGraveSizeRequired: 'Введите размер могилы.',
    errCareTypeRequired: 'Введите тип ухода.',
    errGreenRequired: 'Укажите, есть ли у этой записи зелёная зона.',
    errPriceInvalid: 'Введите цену больше 0.',
    errExists: 'Запись для этого города, размера могилы, типа ухода и выбора зелёной зоны уже существует.',
    confirmDeactivate: 'Деактивировать эту запись? Клиенты больше не смогут выбрать её для нового заказа.',
    seedDefaults: 'Создать стандартные 12 пакетов',
    seeding: 'Создание…',
    seedDone: (created, skipped) => `Создано ${created} новых записей${skipped ? `, пропущено ${skipped} уже существующих` : ''}.`,
    seedNoCities: 'Не удалось найти Bakı и Gəncə в коллекции cities — сначала добавьте эти города.',
    entry: 'Размер могилы / Тип ухода',
    greenCol: 'Зелёная зона',
    priceCol: 'Цена',
  },
};

/** Deterministic, collision-safe doc id -- the same (city, size, type) triple
 * always resolves to the same document, so a repeat "create" cleanly fails
 * as a duplicate instead of silently making a second, confusing row.
 *
 * Each part is percent-encoded, then joined with `|` -- a character
 * encodeURIComponent always escapes, so it can never appear INSIDE an
 * encoded part and the three-way split is always unambiguous. An earlier
 * version stripped everything outside [a-z0-9] to a single "_" separator
 * used both between AND within parts (Codex finding): "A-B"+"Care" and
 * "A B"+"Care" collapsed to the same id, and a Cyrillic-only value (no
 * Latin/diacritic characters to transliterate) collapsed to empty. Percent-
 * encoding preserves every character losslessly, so neither can happen. */
function encodePart(value) {
  return encodeURIComponent(String(value || '').trim().toLowerCase());
}

/** Firestore rejects a document id matching __.*__ (reserved for its own
 * internal use). Vanishingly unlikely with real city/size/type text, but a
 * crafted input like "__city", "size", "care__" would otherwise produce
 * exactly that shape (Codex finding, round 2) and fail the write with a
 * confusing error instead of the normal "already exists" duplicate check. */
function escapeReservedId(id) {
  return /^__.*__$/.test(id) ? `x${id}` : id;
}

/** The ORIGINAL 3-part (city, size, careType) doc id scheme -- unchanged
 * since this feature's first release. Used ONLY by seedDefaults() below, so
 * a repeat click of that button keeps resolving to the exact same 12
 * existing docs it always has. These 12 docs, and this id scheme, are
 * deliberately never produced by the (green-aware) create form any more --
 * see entryDocId -- but must never be renamed/deleted either, since an
 * order already placed (and not yet paid) against one of these ids still
 * needs it to resolve at payment time (functions/order-pricing.js looks the
 * price up by this exact id). */
function legacyEntryDocId(cityId, graveSize, careType) {
  return escapeReservedId(
    `${encodePart(cityId)}|${encodePart(graveSize)}|${encodePart(careType)}`,
  );
}

/** The CURRENT 4-part (city, size, careType, green) doc id scheme -- adds
 * "Green area" (Yaşıllıq sahəsi) as a 4th key segment, the same way size
 * and care type already are. `green` is a real boolean, encoded as the
 * fixed token 'yes'/'no' (never the AZ/RU/EN display label) so the id never
 * depends on which language the admin was using when they created the
 * entry. */
function entryDocId(cityId, graveSize, careType, green) {
  const greenToken = green ? 'yes' : 'no';
  return escapeReservedId(
    `${encodePart(cityId)}|${encodePart(graveSize)}|${encodePart(careType)}|${greenToken}`,
  );
}

/** The 12 initial entries this feature ships with. Prices are exact; the
 * description is the same for a given (graveSize, careType) regardless of
 * city -- only the price differs between Bakı and Gəncə. Keyed by city
 * NAME here (resolved to a real city doc id at seed time), never a
 * hardcoded id, since city ids are assigned by Firestore/admin, not by
 * this code. */
const SEED_DESCRIPTIONS = {
  'Tək məzar|Klassik Baxım': {
    az: 'Tək məzarın əsas təmizlik və qayğı xidməti.',
    ru: 'Базовая услуга уборки и ухода за одной могилой.',
    en: 'Basic cleaning and care service for a single grave.',
  },
  'Tək məzar|Xüsusi Baxım': {
    az: 'Tək məzar üçün geniş həcmli xüsusi qayğı xidməti.',
    ru: 'Расширенная услуга специального ухода за одной могилой.',
    en: 'Extended special care service for a single grave.',
  },
  'Böyük məzar|Klassik Baxım': {
    az: '2-3 nəfərlik və ya iri ölçülü tək məzar üçün əsas təmizlik xidməti.',
    ru: 'Базовая услуга уборки для большой могилы (на 2-3 человека или увеличенного размера).',
    en: 'Basic cleaning service for a large grave (2-3 people or an oversized single grave).',
  },
  'Böyük məzar|Xüsusi Baxım': {
    az: 'Böyük məzar üçün geniş həcmli xüsusi qayğı xidməti.',
    ru: 'Расширенная услуга специального ухода для большой могилы.',
    en: 'Extended special care service for a large grave.',
  },
  'Ailə məzarı|Klassik Baxım': {
    az: 'Ailə məzarı (zərdab/iri ailə məzarı) üçün əsas təmizlik xidməti.',
    ru: 'Базовая услуга уборки для семейной могилы.',
    en: 'Basic cleaning service for a family grave.',
  },
  'Ailə məzarı|Xüsusi Baxım': {
    az: 'Ailə məzarı üçün geniş həcmli xüsusi qayğı xidməti.',
    ru: 'Расширенная услуга специального ухода для семейной могилы.',
    en: 'Extended special care service for a family grave.',
  },
};

const SEED_ROWS = [
  { cityName: 'Gəncə', graveSize: 'Tək məzar', careType: 'Klassik Baxım', price: 30 },
  { cityName: 'Gəncə', graveSize: 'Tək məzar', careType: 'Xüsusi Baxım', price: 50 },
  { cityName: 'Gəncə', graveSize: 'Böyük məzar', careType: 'Klassik Baxım', price: 45 },
  { cityName: 'Gəncə', graveSize: 'Böyük məzar', careType: 'Xüsusi Baxım', price: 75 },
  { cityName: 'Gəncə', graveSize: 'Ailə məzarı', careType: 'Klassik Baxım', price: 60 },
  { cityName: 'Gəncə', graveSize: 'Ailə məzarı', careType: 'Xüsusi Baxım', price: 100 },
  { cityName: 'Bakı', graveSize: 'Tək məzar', careType: 'Klassik Baxım', price: 45 },
  { cityName: 'Bakı', graveSize: 'Tək məzar', careType: 'Xüsusi Baxım', price: 65 },
  { cityName: 'Bakı', graveSize: 'Böyük məzar', careType: 'Klassik Baxım', price: 60 },
  { cityName: 'Bakı', graveSize: 'Böyük məzar', careType: 'Xüsusi Baxım', price: 90 },
  { cityName: 'Bakı', graveSize: 'Ailə məzarı', careType: 'Klassik Baxım', price: 75 },
  { cityName: 'Bakı', graveSize: 'Ailə məzarı', careType: 'Xüsusi Baxım', price: 115 },
];

export default function ServicePackagesPage({ user }) {
  const { lang } = useLang();
  const labels = labelsByLang[lang] || labelsByLang.en;

  const [cities, setCities] = useState([]);
  const [entries, setEntries] = useState([]);
  const [cityTab, setCityTab] = useState('');

  const [cityId, setCityId] = useState('');
  const [graveSize, setGraveSize] = useState('');
  const [careType, setCareType] = useState('');
  // '' = not chosen yet (blocks submit), 'yes' / 'no' once chosen -- kept as
  // the raw token rather than a boolean so the "not chosen" state has its
  // own distinct value instead of overloading `false`.
  const [green, setGreen] = useState('');
  const [price, setPrice] = useState('');
  const [descAz, setDescAz] = useState('');
  const [descRu, setDescRu] = useState('');
  const [descEn, setDescEn] = useState('');
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Editing an existing entry's price/description in place. cityId/graveSize
  // /careType are the doc-id-determining identity fields and stay immutable
  // once created (same as a promo code's `code`) -- to rename the size or
  // care type itself, deactivate this entry and create a new one.
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editDescAz, setEditDescAz] = useState('');
  const [editDescRu, setEditDescRu] = useState('');
  const [editDescEn, setEditDescEn] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const stopCities = onSnapshot(
      collection(db, 'cities'),
      snap => setCities(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setError('Unable to load cities. Please refresh or sign in again.'),
    );
    const stopEntries = onSnapshot(
      collection(db, 'service_packages'),
      snap => setEntries(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
      ),
      () => setError('Unable to load service packages. Please refresh or sign in again.'),
    );
    return () => { stopCities(); stopEntries(); };
  }, []);

  const citiesWithEntries = useMemo(() => {
    const ids = new Set(entries.map(e => e.cityId));
    return cities.filter(c => ids.has(c.id));
  }, [entries, cities]);

  const visibleEntries = cityTab
    ? entries.filter(e => e.cityId === cityTab)
    : entries;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of visibleEntries) {
      const key = e.graveSize || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return Array.from(map.entries());
  }, [visibleEntries]);

  if (!canAccess(user, 'packages')) return <AccessDenied />;

  const cityName = (id) => cities.find(c => c.id === id)?.name || id;

  // '—' for one of the original 12 docs, which predate this dimension and
  // never carry a `greenArea` field at all -- never rendered as a false "No".
  // Field named to match OrderPackageSnapshot.greenArea exactly, same
  // same-name convention careType already follows between this catalog
  // entry and the order snapshot it's frozen onto.
  const greenLabel = (entry) => {
    if (entry.greenArea === true) return labels.greenYes;
    if (entry.greenArea === false) return labels.greenNo;
    return '—';
  };

  async function createEntry(e) {
    e.preventDefault();
    setError('');
    setMsg('');

    if (!cityId) { setError(labels.errCityRequired); return; }
    const size = graveSize.trim();
    const type = careType.trim();
    if (!size) { setError(labels.errGraveSizeRequired); return; }
    if (!type) { setError(labels.errCareTypeRequired); return; }
    if (green !== 'yes' && green !== 'no') { setError(labels.errGreenRequired); return; }
    const greenBool = green === 'yes';
    const value = Number(price);
    if (!Number.isFinite(value) || value <= 0) { setError(labels.errPriceInvalid); return; }

    setCreating(true);
    try {
      const id = entryDocId(cityId, size, type, greenBool);
      const ref = doc(db, 'service_packages', id);
      // A cheap pre-check against the local snapshot for fast feedback --
      // NOT the actual guard. The transaction below is what actually
      // prevents two admins creating the same (city, size, type, green) at
      // once from silently clobbering each other (same pattern as promo
      // codes).
      await runTransaction(db, async tx => {
        const existing = await tx.get(ref);
        if (existing.exists()) throw new Error(labels.errExists);
        tx.set(ref, {
          cityId,
          graveSize: size,
          careType: type,
          greenArea: greenBool,
          price: value,
          description: buildLocalizedTextPayload(descAz, descRu, descEn),
          active: true,
          sortOrder: entries.length,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      setGraveSize(''); setCareType(''); setGreen(''); setPrice('');
      setDescAz(''); setDescRu(''); setDescEn('');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(entry) {
    const willDeactivate = entry.active === true;
    if (willDeactivate && !window.confirm(labels.confirmDeactivate)) return;
    try {
      await updateDoc(doc(db, 'service_packages', entry.id), {
        active: !willDeactivate,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err.message || String(err));
    }
  }

  function startEdit(entry) {
    setError(''); setMsg('');
    setEditingId(entry.id);
    setEditPrice(String(entry.price ?? ''));
    setEditDescAz(entry.description?.az || '');
    setEditDescRu(entry.description?.ru || '');
    setEditDescEn(entry.description?.en || '');
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function saveEdit(entry) {
    setError('');
    const value = Number(editPrice);
    if (!Number.isFinite(value) || value <= 0) { setError(labels.errPriceInvalid); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'service_packages', entry.id), {
        price: value,
        description: buildLocalizedTextPayload(editDescAz, editDescRu, editDescEn),
        updatedAt: serverTimestamp(),
      });
      // Functional update, not a bare setEditingId(null): if the admin
      // opened a DIFFERENT row's editor while this save was still in
      // flight, editingId has already moved on, and unconditionally
      // clearing it would silently close that other row's unsaved editor
      // (Codex finding, round 2).
      setEditingId(current => (current === entry.id ? null : current));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  /** Idempotent: creates only entries that don't already exist at their
   * deterministic id, so a repeat click never duplicates or overwrites an
   * admin's later price edit. Resolves Bakı/Gəncə to their REAL city doc
   * ids by name -- never a hardcoded id, since this repo assigns city ids
   * at creation time, not this script. */
  async function seedDefaults() {
    setError(''); setMsg('');
    const byName = (name) => cities.find(c => (c.name || '').trim() === name);
    const gence = byName('Gəncə');
    const baki = byName('Bakı');
    if (!gence || !baki) { setError(labels.seedNoCities); return; }

    setSeeding(true);
    try {
      let created = 0;
      let skipped = 0;
      for (let index = 0; index < SEED_ROWS.length; index += 1) {
        const row = SEED_ROWS[index];
        const city = row.cityName === 'Bakı' ? baki : gence;
        const id = legacyEntryDocId(city.id, row.graveSize, row.careType);
        const desc = SEED_DESCRIPTIONS[`${row.graveSize}|${row.careType}`] || {};
        // A transaction, not a captured-snapshot check + blind setDoc: another
        // admin creating (or this seed re-running in another tab) between the
        // check and the write must never overwrite a real entry's price or
        // active status (Codex finding).
        const ref = doc(db, 'service_packages', id);
        const wasCreated = await runTransaction(db, async tx => {
          const existing = await tx.get(ref);
          if (existing.exists()) return false;
          tx.set(ref, {
            cityId: city.id,
            graveSize: row.graveSize,
            careType: row.careType,
            price: row.price,
            description: desc,
            active: true,
            sortOrder: index,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          return true;
        });
        if (wasCreated) created += 1; else skipped += 1;
      }
      setMsg(labels.seedDone(created, skipped));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="page-shell">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="page-title">{labels.title}</h1>
          <p className="page-description max-w-2xl">{labels.description}</p>
        </div>
        <button onClick={seedDefaults} disabled={seeding} className="btn-secondary whitespace-nowrap">
          {seeding ? labels.seeding : labels.seedDefaults}
        </button>
      </div>

      <form onSubmit={createEntry} className="rounded-xl border border-gray-100 bg-white p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.city}</label>
          <select className="input-field w-full" value={cityId} onChange={e => setCityId(e.target.value)}>
            <option value="">—</option>
            {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.graveSize}</label>
          <input className="input-field w-full" value={graveSize} onChange={e => setGraveSize(e.target.value)} placeholder={labels.graveSizePlaceholder} />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.careType}</label>
          <input className="input-field w-full" value={careType} onChange={e => setCareType(e.target.value)} placeholder={labels.careTypePlaceholder} />
        </div>
        <div className="w-32">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.green}</label>
          <select className="input-field w-full" value={green} onChange={e => setGreen(e.target.value)}>
            <option value="">{labels.greenChoose}</option>
            <option value="yes">{labels.greenYes}</option>
            <option value="no">{labels.greenNo}</option>
          </select>
        </div>
        <div className="w-28">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.price}</label>
          <input className="input-field w-full" type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descAz}</label>
          <textarea className="input-field w-full" rows="3" value={descAz} onChange={e => setDescAz(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descRu}</label>
          <textarea className="input-field w-full" rows="3" value={descRu} onChange={e => setDescRu(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descEn}</label>
          <textarea className="input-field w-full" rows="3" value={descEn} onChange={e => setDescEn(e.target.value)} />
        </div>
        <button type="submit" disabled={creating} className="btn-primary whitespace-nowrap">
          {creating ? labels.creating : labels.create}
        </button>
      </form>

      {msg && <p className="text-green-600 text-sm mb-4">{msg}</p>}
      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <div className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 mb-5">
        <button
          onClick={() => setCityTab('')}
          className={`px-4 py-2 text-xs font-bold rounded-lg transition ${cityTab === '' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`}
        >
          {labels.allCities}
        </button>
        {citiesWithEntries.map(c => (
          <button
            key={c.id}
            onClick={() => setCityTab(c.id)}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition ${cityTab === c.id ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {grouped.map(([size, rows]) => (
        <div key={size} className="mb-8">
          <h2 className="text-sm font-bold text-gray-900 mb-2">{size}</h2>
          <Table headers={[labels.entry, labels.greenCol, labels.priceCol, labels.status, '']} empty={labels.noData}>
            {rows.map(entry => {
              const isActive = entry.active === true;
              if (entry.id === editingId) {
                return (
                  <tr key={entry.id} className="border-b border-gray-50 bg-gray-50 text-sm align-top">
                    <td className="px-4 py-3" colSpan={5}>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <div className="font-medium">{entry.careType}</div>
                          <div className="text-gray-400 text-xs">{cityName(entry.cityId)} · {greenLabel(entry)}</div>
                        </div>
                        <div className="w-28">
                          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.price}</label>
                          <input className="input-field w-full" type="number" min="0" step="0.01" value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descAz}</label>
                          <textarea className="input-field w-full" rows="3" value={editDescAz} onChange={e => setEditDescAz(e.target.value)} />
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descRu}</label>
                          <textarea className="input-field w-full" rows="3" value={editDescRu} onChange={e => setEditDescRu(e.target.value)} />
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descEn}</label>
                          <textarea className="input-field w-full" rows="3" value={editDescEn} onChange={e => setEditDescEn(e.target.value)} />
                        </div>
                        <button onClick={() => saveEdit(entry)} disabled={saving} className="btn-primary whitespace-nowrap">
                          {saving ? labels.creating : labels.save}
                        </button>
                        <button onClick={cancelEdit} className="btn-secondary whitespace-nowrap">{labels.cancel}</button>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={entry.id} className="border-b border-gray-50 hover:bg-gray-50 text-sm align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{entry.careType}</div>
                    <div className="text-gray-400 text-xs">{cityName(entry.cityId)}</div>
                    {entry.description?.[lang] && (
                      <div className="text-gray-500 text-xs mt-1 max-w-md whitespace-pre-line">{entry.description[lang]}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{greenLabel(entry)}</td>
                  <td className="px-4 py-3 font-bold text-primary whitespace-nowrap">{entry.price} ₼</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {isActive ? labels.active : labels.inactive}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      onClick={() => startEdit(entry)}
                      className="text-xs font-semibold text-primary hover:underline mr-3"
                    >
                      {labels.edit}
                    </button>
                    <button
                      onClick={() => toggleActive(entry)}
                      className={`text-xs font-semibold ${isActive ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'}`}
                    >
                      {isActive ? labels.deactivate : labels.reactivate}
                    </button>
                  </td>
                </tr>
              );
            })}
          </Table>
        </div>
      ))}
    </div>
  );
}
